import { FunctionComponent } from 'react';
import { MultiSelect } from '../context/selectors/multi.select';
import { RadioGroupSelector } from '../context/selectors/radio.group';
import { SingleSelect } from '../context/selectors/single.select';
import { UnknownSelector } from '../context/selectors/unknown';
import { ContextSettingSelectorProps } from '../context/types';
import { DummyContent } from '../elements/dummy';
import { DummyStaticContent } from '../elements/dummy.static';
import { DataErrorElement } from '../elements/error/error';
import { KeyIndicators } from '../elements/keyIndicators/indicators';
import { Leaderboard } from '../elements/leaderboard/leaderboard';
import { MultiKpi } from '../elements/multiKpi/multiKpi';
import { Segmentation } from '../elements/segmentation/segmentation';
import { StatsTable } from '../elements/statsTable/statsTable';
import { TimeSeries } from '../elements/timeSeries/timeSeries';
import { UnknownElement } from '../elements/unknown/unknown';
import { CoreContents, SettingSelector } from '../types';
import { ContentProps } from './types';
import { KpiTrends } from '../elements/kpiTrends/trends';
import { Billboard } from '../elements/billboard/billboard';
import { AlertPanel } from '../elements/alertPanel/alertPanel';
import { SingleKpiTrend } from '../elements/singleKpiTrend/trend';
import { Heatmap } from '../elements/heatmap/heatmap';
import { KpiDecomposition } from '../elements/kpiDecomposition/kpiDecomposition';
import { UpsellContentElement } from '../elements/upsell/upsell.content';

// register all content elements - components that are used to render content
// this is just a registry and the rendering is done in the ContentElement component in element.tsx
export class ContentProviderRegister {
  private static _instance: ContentProviderRegister;

  private contextSettingSelectors: Record<string, FunctionComponent<ContextSettingSelectorProps>> = {};
  private contents: Record<string, FunctionComponent<ContentProps>> = {};

  private constructor() {}

  registerContent(element: FunctionComponent<ContentProps>, identifier: string) {
    this.contents[identifier] = element;
  }

  getContent(identifier): FunctionComponent<ContentProps> {
    return this.contents[identifier] || UnknownElement;
  }

  registerContextSettingSelector(element: FunctionComponent<ContextSettingSelectorProps>, identifier: string) {
    this.contextSettingSelectors[identifier] = element;
  }

  getContextSettingsSelector(
    ofType: SettingSelector,
    identifier: string
  ): FunctionComponent<ContextSettingSelectorProps> {
    let selector: FunctionComponent<ContextSettingSelectorProps> | undefined;
    if (ofType === SettingSelector.custom) {
      selector = this.contextSettingSelectors[identifier];
    } else {
      selector = this.contextSettingSelectors[ofType];
    }
    return selector || UnknownSelector;
  }

  static getInstance(): ContentProviderRegister {
    if (!ContentProviderRegister._instance) {
      ContentProviderRegister._instance = new ContentProviderRegister();
      prefill();
    }
    return ContentProviderRegister._instance;
  }
}

function prefill() {
  ContentProviderRegister.getInstance().registerContent(DataErrorElement, CoreContents.dataError);
  ContentProviderRegister.getInstance().registerContent(TimeSeries, CoreContents.timeSeries);
  ContentProviderRegister.getInstance().registerContent(Segmentation, CoreContents.segmentation);
  ContentProviderRegister.getInstance().registerContent(MultiKpi, CoreContents.multiKpi);
  ContentProviderRegister.getInstance().registerContent(Leaderboard, CoreContents.leaderBoard);
  ContentProviderRegister.getInstance().registerContent(StatsTable, CoreContents.statsTable);
  ContentProviderRegister.getInstance().registerContent(KeyIndicators, CoreContents.keyIndicators);
  ContentProviderRegister.getInstance().registerContent(KpiTrends, CoreContents.kpiTrends);
  ContentProviderRegister.getInstance().registerContent(Billboard, CoreContents.billboard);
  ContentProviderRegister.getInstance().registerContent(AlertPanel, CoreContents.alertPanel);
  ContentProviderRegister.getInstance().registerContent(SingleKpiTrend, CoreContents.singleKpiTrend);
  ContentProviderRegister.getInstance().registerContent(Heatmap, CoreContents.heatmap);
  ContentProviderRegister.getInstance().registerContent(KpiDecomposition, CoreContents.kpiDecompositon);

  // Register custom upsell content
  ContentProviderRegister.getInstance().registerContent(UpsellContentElement, 'upsell');

  ContentProviderRegister.getInstance().registerContent(DummyContent, 'dummy');
  ContentProviderRegister.getInstance().registerContent(DummyStaticContent, 'dummy-static');

  // context selectors - highligted brand modal
  ContentProviderRegister.getInstance().registerContextSettingSelector(SingleSelect, SettingSelector.singleSelect);
  ContentProviderRegister.getInstance().registerContextSettingSelector(MultiSelect, SettingSelector.multiSelect);
  ContentProviderRegister.getInstance().registerContextSettingSelector(RadioGroupSelector, SettingSelector.radioGroup);
}
